import { useState } from 'react';
import { useQuery, useMutation } from '@apollo/client/react';
import { gql } from '@apollo/client';
import { useNavigate, useParams } from 'react-router-dom';
import { Navigation } from '../components/Navigation';
import { Button } from '../components/ui/button';
import { Card, CardHeader, CardTitle, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { AppointmentModal } from '../components/calendar/AppointmentModal';
import { ClientManagementActions } from '../components/admin/ClientManagementActions';
import { ClientDocumentsTable } from '../components/ClientDocumentsTable';
import { AccountStatusToggle } from '../components/user/AccountStatusToggle';
import { ScheduleConferenceModal } from '../components/video/ScheduleConferenceModal';
import { CASE_TYPES } from '../constants/caseTypes';
import { GET_INQUIRIES } from '../graphql/queries';

const GET_CURRENT_USER = gql`
  query GetCurrentUser {
    getCurrentUser {
      id
      email
      firstName
      lastName
      role
    }
  }
`;

const GET_USER = gql`
  query GetUser($id: ID!) {
    getUser(id: $id) {
      id
      email
      firstName
      lastName
      phoneNumber
      role
      isEnabled
      loginDisabled
      address {
        street1
        street2
        city
        state
        zipCode
        country
      }
      createdAt
      updatedAt
    }
  }
`;

const LIST_CASES = gql`
  query ListCases {
    listCases {
      items {
        id
        caseNumber
        clientId
        status
        caseType
        description
        incidentDate
        createdAt
        assignedAttorney {
          id
          firstName
          lastName
        }
        assignedCaseManager {
          id
          firstName
          lastName
        }
      }
    }
  }
`;

const CREATE_CASE = gql`
  mutation CreateCase($input: CreateCaseInput!) {
    createCase(input: $input) {
      id
      caseNumber
      status
    }
  }
`;

// Type definitions for Apollo queries/mutations
interface CurrentUser {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  role: string;
}

interface GetCurrentUserData {
  getCurrentUser: CurrentUser;
}

interface User {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  phoneNumber?: string;
  role: string;
  isEnabled?: boolean;
  loginDisabled?: boolean;
  address?: {
    street1?: string;
    street2?: string;
    city?: string;
    state?: string;
    zipCode?: string;
    country?: string;
  };
  createdAt: string;
  updatedAt: string;
}

interface GetUserData {
  getUser: User;
}

interface Case {
  id: string;
  caseNumber: string;
  clientId: string;
  status: string;
  caseType: string;
  description?: string;
  incidentDate?: string;
  createdAt: string;
  assignedAttorney?: {
    id: string;
    firstName: string;
    lastName: string;
  };
  assignedCaseManager?: {
    id: string;
    firstName: string;
    lastName: string;
  };
}

interface ListCasesData {
  listCases: {
    items: Case[];
  };
}

interface Inquiry {
  id: string;
  userId?: string;
  [key: string]: any;
}

interface ListInquiriesData {
  listInquiries: {
    items: Inquiry[];
  };
}

interface CreateCaseData {
  createCase: {
    id: string;
    caseNumber: string;
    status: string;
  };
}

export function ClientDetail() {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [activeTab, setActiveTab] = useState<'overview' | 'cases' | 'inquiries' | 'documents' | 'activity'>('overview');
  const [showAppointmentModal, setShowAppointmentModal] = useState(false);
  const [showVideoConferenceModal, setShowVideoConferenceModal] = useState(false);
  const [showCreateCase, setShowCreateCase] = useState(false);
  const [caseType, setCaseType] = useState('');
  const [customCaseType, setCustomCaseType] = useState('');
  const [description, setDescription] = useState('');
  const [incidentDate, setIncidentDate] = useState('');

  const { data: userData } = useQuery<GetCurrentUserData>(GET_CURRENT_USER);
  const { data: clientData, loading: clientLoading, refetch: refetchClient } = useQuery<GetUserData>(GET_USER, {
    variables: { id },
    skip: !id,
    fetchPolicy: 'cache-and-network',
  });
  const { data: casesData, loading: casesLoading, refetch: refetchCases } = useQuery<ListCasesData>(LIST_CASES, {
    variables: { limit: 1000 },
    fetchPolicy: 'cache-and-network',
  });

  const { data: inquiriesData, loading: inquiriesLoading, refetch: refetchInquiries } = useQuery<ListInquiriesData>(GET_INQUIRIES, {
    variables: { limit: 500 },
    fetchPolicy: 'cache-and-network',
  });

  const user = userData?.getCurrentUser;
  const client = clientData?.getUser;
  const allCases = casesData?.listCases?.items || [];
  const clientCases = allCases.filter((c: any) => c.clientId === id && c.status !== 'INQUIRY');
  const allInquiries = inquiriesData?.listInquiries?.items || [];
  const clientInquiries = allInquiries.filter((inq: any) => inq.userId === id);

  const [createCase, { loading: creatingCase }] = useMutation<CreateCaseData>(CREATE_CASE);

  const handleRefresh = async () => {
    await Promise.all([refetchClient(), refetchCases(), refetchInquiries()]);
  };

  const handleCreateCase = async (e: React.FormEvent) => {
    e.preventDefault();
    
    const finalCaseType = caseType === 'Other' ? customCaseType : caseType;
    
    if (!finalCaseType.trim() || !description.trim()) {
      alert('Please fill in all required fields');
      return;
    }

    try {
      const result = await createCase({
        variables: {
          input: {
            clientId: id,
            caseType: finalCaseType,
            description,
            incidentDate: incidentDate || undefined,
          },
        },
        refetchQueries: ['ListCases'],
      });
      
      setCaseType('');
      setCustomCaseType('');
      setDescription('');
      setIncidentDate('');
      setShowCreateCase(false);
      
      // Navigate to the new case
      if (result.data?.createCase?.id) {
        navigate(`/cases/${result.data.createCase.id}`);
      } else {
        alert('Case created successfully!');
        await handleRefresh();
      }
    } catch (error: any) {
      console.error('Error creating case:', error);
      alert('Error creating case: ' + error.message);
    }
  };

  if (clientLoading || casesLoading || inquiriesLoading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-aalaw-red"></div>
      </div>
    );
  }

  if (!client) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center">
          <h2 className="text-xl font-bold text-red-600">Client not found</h2>
          <Button onClick={() => navigate('/clients')} className="mt-4">
            Back to Clients
          </Button>
        </div>
      </div>
    );
  }

  const activeCases = clientCases.filter((c: any) => c.status !== 'CLOSED');
  const closedCases = clientCases.filter((c: any) => c.status === 'CLOSED');

  const getStatusColor = (status: string) => {
    const colors: Record<string, string> = {
      NEW: 'bg-blue-100 text-blue-700',
      IN_PROGRESS: 'bg-yellow-100 text-yellow-700',
      NEGOTIATION: 'bg-purple-100 text-purple-700',
      LITIGATION: 'bg-orange-100 text-orange-700',
      SETTLED: 'bg-green-100 text-green-700',
      CLOSED: 'bg-gray-100 text-gray-700',
    };
    return colors[status] || 'bg-gray-100 text-gray-700';
  };

  const getInquiryStatusColor = (status: string) => {
    const colors: Record<string, string> = {
      NEW: 'bg-blue-100 text-blue-700',
      REVIEWING: 'bg-yellow-100 text-yellow-700',
      ACCEPTED: 'bg-green-100 text-green-700',
      REJECTED: 'bg-red-100 text-red-700',
    };
    return colors[status] || 'bg-gray-100 text-gray-700';
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Navigation user={user} />

      {/* Header */}
      <header className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-4">
              <Button variant="ghost" onClick={() => navigate(-1)}>
                ← Back
              </Button>
              <h1 className="text-xl font-bold text-aalaw-charcoal">Client Details</h1>
              <p className="text-sm text-gray-600">{client.firstName} {client.lastName}</p>
              {client.isEnabled === false && (
                <Badge variant="secondary" className="bg-red-100 text-red-700">
                  Account Disabled
                </Badge>
              )}
            </div>
            <div className="flex items-center gap-4">
              <Button
                variant="outline"
                size="sm"
                onClick={handleRefresh}
              >
                🔄 Refresh
              </Button>
              <ClientManagementActions
                clientId={client.id}
                clientName={`${client.firstName} ${client.lastName}`}
                clientEmail={client.email}
                isEnabled={client.isEnabled !== false}
                userRole={user?.role}
                onEdit={() => navigate(`/clients/${client.id}/edit`)}
                onRefresh={handleRefresh}
              />
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left Column - Client Info */}
          <div className="lg:col-span-1 space-y-6">
            {/* Client Profile Card */}
            <Card>
              <CardContent className="pt-6">
                <div className="text-center mb-6">
                  <div className="w-24 h-24 rounded-full bg-aalaw-red flex items-center justify-center mx-auto mb-4">
                    <span className="text-white font-bold text-3xl">
                      {client.firstName?.[0]?.toUpperCase() || client.email?.[0]?.toUpperCase() || '?'}
                    </span>
                  </div>
                  <h2 className="text-2xl font-bold text-gray-900">
                    {client.firstName} {client.lastName}
                  </h2>
                  <p className="text-sm text-gray-600 mt-1 capitalize">{client.role.toLowerCase()}</p>
                </div>

                {/* Contact Information */}
                <div className="space-y-3 border-t border-gray-200 pt-4">
                  {/* Account Status Toggle */}
                  <AccountStatusToggle
                    userId={client.id}
                    isEnabled={client.isEnabled !== false}
                    loginDisabled={client.loginDisabled === true}
                    userName={`${client.firstName} ${client.lastName}`}
                    onStatusChange={() => refetchClient()}
                  />
                  <div>
                    <p className="text-xs text-gray-500 mb-1">Email</p>
                    <a
                      href={`mailto:${client.email}`}
                      className="text-sm text-aalaw-red hover:underline"
                    >
                      {client.email}
                    </a>
                  </div>
                  {client.phoneNumber && (
                    <div>
                      <p className="text-xs text-gray-500 mb-1">Phone</p>
                      <a
                        href={`tel:${client.phoneNumber}`}
                        className="text-sm text-aalaw-red hover:underline"
                      >
                        {client.phoneNumber}
                      </a>
                    </div>
                  )}
                  {client.address && (
                    <div>
                      <p className="text-xs text-gray-500 mb-1">Address</p>
                      <div className="text-sm text-gray-900">
                        <p>{client.address.street1}</p>
                        {client.address.street2 && <p>{client.address.street2}</p>}
                        <p>
                          {client.address.city}, {client.address.state} {client.address.zipCode}
                        </p>
                        {client.address.country && client.address.country !== 'US' && (
                          <p>{client.address.country}</p>
                        )}
                      </div>
                    </div>
                  )}
                  <div>
                    <p className="text-xs text-gray-500 mb-1">Client Since</p>
                    <p className="text-sm text-gray-900">
                      {new Date(client.createdAt).toLocaleDateString('en-US', {
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric',
                      })}
                    </p>
                  </div>
                </div>

                {/* Quick Actions */}
                <div className="grid grid-cols-2 gap-2 mt-6 pt-4 border-t border-gray-200">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => window.location.href = `tel:${client.phoneNumber}`}
                    disabled={!client.phoneNumber}
                  >
                    📞 Call
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => window.location.href = `mailto:${client.email}`}
                  >
                    ✉️ Email
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => alert('SMS feature coming soon')}
                  >
                    💬 Text
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      if (clientCases.length > 0) {
                        setShowAppointmentModal(true);
                      } else {
                        alert('Please create a case first before scheduling an appointment');
                      }
                    }}
                  >
                    📅 Schedule
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setShowVideoConferenceModal(true)}
                    className="col-span-2"
                  >
                    📹 Video Conference
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Stats Card */}
            <Card>
              <CardHeader>
                <CardTitle>Statistics</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-600">Total Cases</span>
                    <span className="text-lg font-bold text-gray-900">{clientCases.length}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-600">Active Cases</span>
                    <span className="text-lg font-bold text-blue-600">{activeCases.length}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-600">Closed Cases</span>
                    <span className="text-lg font-bold text-gray-600">{closedCases.length}</span>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Right Column - Cases and Activity */}
          <div className="lg:col-span-2 space-y-6">
            {/* Tabs */}
            <div className="flex gap-2 border-b border-gray-200">
              <button
                onClick={() => setActiveTab('overview')}
                className={`px-4 py-2 font-medium text-sm border-b-2 transition-colors ${
                  activeTab === 'overview'
                    ? 'border-aalaw-red text-aalaw-red'
                    : 'border-transparent text-gray-600 hover:text-gray-900'
                }`}
              >
                Overview
              </button>
              <button
                onClick={() => setActiveTab('inquiries')}
                className={`px-4 py-2 font-medium text-sm border-b-2 transition-colors ${
                  activeTab === 'inquiries'
                    ? 'border-aalaw-red text-aalaw-red'
                    : 'border-transparent text-gray-600 hover:text-gray-900'
                }`}
              >
                Inquiries ({clientInquiries.length})
              </button>
              <button
                onClick={() => setActiveTab('cases')}
                className={`px-4 py-2 font-medium text-sm border-b-2 transition-colors ${
                  activeTab === 'cases'
                    ? 'border-aalaw-red text-aalaw-red'
                    : 'border-transparent text-gray-600 hover:text-gray-900'
                }`}
              >
                Cases ({clientCases.length})
              </button>
              <button
                onClick={() => setActiveTab('documents')}
                className={`px-4 py-2 font-medium text-sm border-b-2 transition-colors ${
                  activeTab === 'inquiries'
                    ? 'border-aalaw-red text-aalaw-red'
                    : 'border-transparent text-gray-600 hover:text-gray-900'
                }`}
              >
                Inquiries ({clientInquiries.length})
              </button>
              <button
                onClick={() => setActiveTab('documents')}
                className={`px-4 py-2 font-medium text-sm border-b-2 transition-colors ${
                  activeTab === 'documents'
                    ? 'border-aalaw-red text-aalaw-red'
                    : 'border-transparent text-gray-600 hover:text-gray-900'
                }`}
              >
                Documents
              </button>
              <button
                onClick={() => setActiveTab('activity')}
                className={`px-4 py-2 font-medium text-sm border-b-2 transition-colors ${
                  activeTab === 'activity'
                    ? 'border-aalaw-red text-aalaw-red'
                    : 'border-transparent text-gray-600 hover:text-gray-900'
                }`}
              >
                Activity
              </button>
            </div>

            {/* Tab Content */}
            {activeTab === 'overview' && (
              <div className="space-y-6">
                <Card>
                  <CardHeader>
                    <CardTitle>Recent Cases</CardTitle>
                  </CardHeader>
                  <CardContent>
                    {clientCases.length === 0 ? (
                      <div className="text-center py-8">
                        <div className="text-4xl mb-3">📋</div>
                        <p className="text-gray-500 text-sm mb-4">No cases yet</p>
                        <Button onClick={() => setShowCreateCase(true)}>
                          Create New Case
                        </Button>
                      </div>
                    ) : (
                      <div className="space-y-3">
                        {clientCases.slice(0, 5).map((caseItem: any) => (
                          <div
                            key={caseItem.id}
                            onClick={() => navigate(`/cases/${caseItem.id}`)}
                            className="flex items-center justify-between p-4 rounded-lg border border-gray-200 hover:border-aalaw-red hover:bg-gray-50 cursor-pointer transition-all"
                          >
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-1">
                                <p className="font-semibold text-gray-900">{caseItem.caseNumber}</p>
                                <span className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(caseItem.status)}`}>
                                  {caseItem.status.replace('_', ' ')}
                                </span>
                              </div>
                              <p className="text-sm text-gray-600">{caseItem.caseType}</p>
                              <p className="text-xs text-gray-500 mt-1">
                                Created {new Date(caseItem.createdAt).toLocaleDateString()}
                              </p>
                            </div>
                            <span className="text-gray-400">›</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>
              </div>
            )}

            {activeTab === 'cases' && (
              <Card>
                <CardHeader className="flex flex-row items-center justify-between">
                  <CardTitle>All Cases</CardTitle>
                  <Button size="sm" onClick={() => setShowCreateCase(true)}>
                    + New Case
                  </Button>
                </CardHeader>
                <CardContent>
                  {clientCases.length === 0 ? (
                    <div className="text-center py-8">
                      <div className="text-4xl mb-3">📋</div>
                      <p className="text-gray-500 text-sm mb-4">No cases yet</p>
                      <Button onClick={() => setShowCreateCase(true)}>
                        Create First Case
                      </Button>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {clientCases.map((caseItem: any) => (
                        <div
                          key={caseItem.id}
                          onClick={() => navigate(`/cases/${caseItem.id}`)}
                          className="p-4 rounded-lg border border-gray-200 hover:border-aalaw-red hover:bg-gray-50 cursor-pointer transition-all"
                        >
                          <div className="flex items-start justify-between mb-2">
                            <div>
                              <div className="flex items-center gap-2 mb-1">
                                <p className="font-bold text-gray-900">{caseItem.caseNumber}</p>
                                <span className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(caseItem.status)}`}>
                                  {caseItem.status.replace('_', ' ')}
                                </span>
                              </div>
                              <p className="text-sm font-medium text-gray-700">{caseItem.caseType}</p>
                            </div>
                          </div>
                          <p className="text-sm text-gray-600 mb-3">{caseItem.description}</p>
                          <div className="flex items-center gap-4 text-xs text-gray-500">
                            <span>Created {new Date(caseItem.createdAt).toLocaleDateString()}</span>
                            {caseItem.incidentDate && (
                              <span>Incident {new Date(caseItem.incidentDate).toLocaleDateString()}</span>
                            )}
                            {caseItem.assignedAttorney && (
                              <span>
                                Attorney: {caseItem.assignedAttorney.firstName} {caseItem.assignedAttorney.lastName}
                              </span>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            )}

            {activeTab === 'inquiries' && (
              <Card>
                <CardHeader>
                  <CardTitle>All Inquiries</CardTitle>
                </CardHeader>
                <CardContent>
                  {clientInquiries.length === 0 ? (
                    <div className="text-center py-8">
                      <div className="text-4xl mb-3">📝</div>
                      <p className="text-gray-500 text-sm">No inquiries found for this client</p>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {clientInquiries.map((inquiry: any) => (
                        <div
                          key={inquiry.id}
                          onClick={() => navigate(`/inquiries/${inquiry.id}`)}
                          className="p-4 rounded-lg border border-gray-200 hover:border-aalaw-red hover:bg-gray-50 cursor-pointer transition-all"
                        >
                          <div className="flex items-start justify-between mb-2">
                            <div>
                              <div className="flex items-center gap-2 mb-1">
                                <p className="font-bold text-gray-900">{inquiry.incidentType}</p>
                                <span className={`px-2 py-1 text-xs font-medium rounded-full ${getInquiryStatusColor(inquiry.status)}`}>
                                  {inquiry.status}
                                </span>
                              </div>
                              <p className="text-sm text-gray-600">
                                {inquiry.firstName} {inquiry.lastName}
                              </p>
                            </div>
                          </div>
                          {inquiry.description && (
                            <p className="text-sm text-gray-600 mb-3 line-clamp-2">{inquiry.description}</p>
                          )}
                          <div className="flex items-center gap-4 text-xs text-gray-500">
                            <span>Created {new Date(inquiry.createdAt).toLocaleDateString()}</span>
                            {inquiry.incidentDate && (
                              <span>Incident {new Date(inquiry.incidentDate).toLocaleDateString()}</span>
                            )}
                            {inquiry.assignedToName && (
                              <span>Assigned to: {inquiry.assignedToName}</span>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            )}

            {activeTab === 'documents' && (
              <ClientDocumentsTable 
                clientCases={clientCases}
                userRole={user?.role}
              />
            )}

            {activeTab === 'activity' && (
              <Card>
                <CardHeader>
                  <CardTitle>Recent Activity</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-center py-8">
                    <div className="text-4xl mb-3">📊</div>
                    <p className="text-gray-500 text-sm">Activity tracking coming soon</p>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </main>

      {/* Appointment Modal */}
      {showAppointmentModal && (
        <AppointmentModal
          preSelectedCaseId={activeCases.length > 0 ? activeCases[0].id : clientCases[0]?.id}
          onClose={() => setShowAppointmentModal(false)}
        />
      )}

      {/* Create Case Modal */}
      {showCreateCase && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
            <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
              <h3 className="text-lg font-semibold text-aalaw-charcoal">Create New Case</h3>
              <button
                onClick={() => {
                  setShowCreateCase(false);
                  setCaseType('');
                  setCustomCaseType('');
                  setDescription('');
                  setIncidentDate('');
                }}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <form onSubmit={handleCreateCase} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Client
                </label>
                <div className="px-3 py-2 bg-gray-50 border border-gray-300 rounded-md text-sm text-gray-700">
                  {client?.firstName} {client?.lastName} ({client?.email})
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Case Type <span className="text-red-500">*</span>
                </label>
                <select
                  value={caseType}
                  onChange={(e) => {
                    setCaseType(e.target.value);
                    if (e.target.value !== 'Other') {
                      setCustomCaseType('');
                    }
                  }}
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-aalaw-red focus:border-aalaw-red"
                >
                  <option value="">Select a case type</option>
                  {CASE_TYPES.map((type) => (
                    <option key={type} value={type}>
                      {type}
                    </option>
                  ))}
                </select>
              </div>

              {caseType === 'Other' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Custom Case Type <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={customCaseType}
                    onChange={(e) => setCustomCaseType(e.target.value)}
                    placeholder="Enter custom case type"
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-aalaw-red focus:border-aalaw-red"
                  />
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Incident Date
                </label>
                <input
                  type="date"
                  value={incidentDate}
                  onChange={(e) => setIncidentDate(e.target.value)}
                  max={new Date().toISOString().split('T')[0]}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-aalaw-red focus:border-aalaw-red"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Description <span className="text-red-500">*</span>
                </label>
                <textarea
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="Brief case description..."
                  required
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-aalaw-red focus:border-aalaw-red"
                />
              </div>

              <div className="flex gap-2 pt-4">
                <Button type="submit" disabled={creatingCase} className="flex-1">
                  {creatingCase ? 'Creating...' : 'Create Case'}
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    setShowCreateCase(false);
                    setCaseType('');
                    setCustomCaseType('');
                    setDescription('');
                    setIncidentDate('');
                  }}
                  className="flex-1"
                >
                  Cancel
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Schedule Video Conference Modal */}
      <ScheduleConferenceModal
        isOpen={showVideoConferenceModal}
        onClose={() => setShowVideoConferenceModal(false)}
        onCreated={() => {
          setShowVideoConferenceModal(false);
        }}
        preSelectedClient={{
          id: client?.id || '',
          firstName: client?.firstName || '',
          lastName: client?.lastName || '',
          email: client?.email,
          phoneNumber: client?.phoneNumber,
        }}
      />
    </div>
  );
}
